import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  EffectRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { PanelModule } from 'primeng/panel';
import { DmRangeDatePickerLibComponent } from '@everymatrix-base-components/dm-range-date-picker-lib';
import { DmInputTextLibComponent } from '@everymatrix-base-components/dm-input-text-lib';
import { DmSelectLibComponent } from '@everymatrix-base-components/dm-select-lib';
import { DmButtonLibComponent } from '@everymatrix-base-components/dm-button-lib';
import { Checkbox } from 'primeng/checkbox';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormsModule,
} from '@angular/forms';
import { AdditionalParams, RulesUsageService } from './services/rules-usage.service';
import { DmTreeTableComponent } from '@everymatrix-base-components/dm-tree-table';
import { catchError, filter, finalize, fromEvent, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { CommonModule, NgStyle } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import {
  downloadURL,
  ReDynamicFilters,
  ReLookups,
  REPORTS,
  ReportTemplate,
  ShadowDomStyleInjectorService,
  TableHeaderColumn,
  TemplateService,
  SessionService,
  TracingInfoService,
  ReportSearchParams,
  TEMPORARY_TEMPLATE_WARNING,
  ReGroupedLookups,
  GlobalMessageService
} from '@everymatrix-base-components/utils';
import { TooltipModule } from 'primeng/tooltip';
import moment from 'moment';
import {
  DomainsOverloadModalComponent,
  SaveTemplateModalComponent,
  TemplateSelectModalComponent,
  ToastMessageComponent,
} from '@everymatrix-base-components/components';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
interface PropertyBooleanStatus {[key: string]: boolean }
interface Lookups { [key: string]: ReLookups[] }
interface GroupedLookups { [key: string]: ReGroupedLookups[] }

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    PanelModule,
    DmRangeDatePickerLibComponent,
    DmInputTextLibComponent,
    DmSelectLibComponent,
    DmButtonLibComponent,
    ReactiveFormsModule,
    DmTreeTableComponent,
    ProgressSpinnerModule,
    NgStyle,
    TooltipModule,
    ToastMessageComponent,
    FormsModule,
    SaveTemplateModalComponent,
    TemplateSelectModalComponent,
    DomainsOverloadModalComponent,
    Checkbox,
    CommonModule,
  ],
  providers: [TemplateService],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class AppComponent implements OnInit, AfterViewInit {
  private templateService = inject(TemplateService);
  private rulesUsageService = inject(RulesUsageService);
  private sessionService = inject(SessionService);
  private tracingInfoService = inject(TracingInfoService);
  private globalMessageService = inject(GlobalMessageService);
  private el = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private shadowDomStyleInjectorService = inject(ShadowDomStyleInjectorService);
  attachedToShadowDom = signal(false);
  isTemplateSaved = signal(true);
  csvLoading = signal(false);
  templateCsvLoadingConfig = signal({ templateId: '', isLoading: false });
  modalVisible = signal(false);
  domainsOverloadVisible = signal(false);
  lookupsSignal = signal<Lookups | undefined>(undefined);
  groupedLookupsSignal = signal<GroupedLookups | undefined>(undefined);
  lookupsViewSignal = signal<Lookups | undefined>(undefined);
  groupedLookupsViewSignal = signal<GroupedLookups | undefined>(undefined);
  domainLookupsSignal = signal<ReLookups[] | undefined>(undefined);
  allFieldsSignal = signal<ReDynamicFilters[] | undefined>(undefined);
  templateSignal = this.templateService.template;
  ready = computed(() => {
    return (
      this.templateSignal() !== undefined &&
      this.lookupsSignal() !== undefined &&
      this.domainLookupsSignal() !== undefined &&
      this.allFieldsSignal() !== undefined &&
      this.lookupsViewSignal() !== undefined &&
      this.groupedLookupsViewSignal() !== undefined
    );
  });

  getFilledFieldNames = computed(() => {
    return ((this.allFieldsSignal() as ReDynamicFilters[]) ?? [])
      .filter(({ field_id }) => {
        const value = this.filterForm.value[field_id];
        if (value == null) {
          return false;
        }
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'string') {
          return value.length > 0;
        }
        return true;
      })
      .map(({ field_name }) => field_name)
      .join(', ');
    
  });

  tableColumns: TableHeaderColumn[] = [];
  filterForm: any;
  isPanelOpened = signal(true);
  tableData: any[] = [];
  cachedPages: { [key: string]: any[] } = {};
  generalTotal = '';
  limit = signal(100);
  offset = 0;
  scrollHeight = 350;
  showReportTable = signal(false);
  tableLoading = signal(false);
  runState = signal(false);
  selectedDomainsKey = 'domain';
  domainsOverloadSaveMode = signal(false);
  domainsOverloadAction: 'Run' | 'Download' | 'Save' | '' = '';
  orderBy: any;
  orderDirection: any;
  reportId = REPORTS.DM_RULES_USAGE.reportId;
  effectRef: EffectRef;
  isScopeDisabled: PropertyBooleanStatus = {};
  isFieldLoading: PropertyBooleanStatus = {};
  isLookupGrouped: PropertyBooleanStatus = {};
  booleanLookup: { label: string; value: boolean }[] = [
    { label: 'True', value: true },
    { label: 'False', value: false },
  ];

  isLoadingLookups = signal(false);

  readonly lookupsByField = computed(() => {
    const grouped = this.groupedLookupsViewSignal();
    const flat = this.lookupsViewSignal();

    return (fieldId: string, isGrouped: boolean) =>
      isGrouped ? grouped?.[fieldId] : flat?.[fieldId];
  });

  constructor() {
    this.sessionService.resetSession();
    this.tracingInfoService.resetTracingInfo();
    this.effectRef = effect(
      () => {
        if (!this.ready() || this.filterForm) {
          return;
        }
        const tpl = this.templateSignal();
        const all = this.allFieldsSignal();
        this.buildFilterForm(
          all as ReDynamicFilters[],
          tpl?.input_params.fieldValues
        );
        this.handleScopes();
        this.showReportTable.set(false);
        this.tableLoading.set(false);
        this.effectRef.destroy();
      },
      { manualCleanup: true }
    );
  }

  ngOnInit() {
    this.initializeApp();
    //Executed when the back/forward buttons are pressed
    fromEvent(window, 'popstate')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.handleRouteChange());

    this.templateService.templateChangedSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((template: ReportTemplate | null) => {
        if (template) {
          this.updateReport(template);
        }
      });
  }

  private initializeApp() {
    this.sessionService.sessionData$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => data !== null),
        take(1),
        switchMap(() =>
          this.loadSchemas$().pipe(
            switchMap((schemas) =>
              this.processTemplateIdFromUrl$().pipe(
                switchMap((template) =>
                  this.loadLookups$(
                    schemas,
                    this.getQueryParams(
                      template?.input_params.fieldValues,
                      schemas
                    )
                  )
                )
              )
            )
          )
        )
      )
      .subscribe();
  }

  loadSchemas$() {
    return this.rulesUsageService.getSchemas().pipe(
      tap((schemas) => {
        this.allFieldsSignal.set(schemas);
      }),
      catchError(() => {
        this.allFieldsSignal.set([]);
        return of([]);
      })
    )
  }

  loadLookups$(
    parameters: ReDynamicFilters[],
    selectedParams: AdditionalParams
  ) {
    return this.rulesUsageService.getLookups$(parameters, selectedParams).pipe(
      map((lookupsResult) => {
        const groupedLookups: GroupedLookups = {};
        Object.keys(lookupsResult).forEach((lookupKey) => {
          this.isLookupGrouped[lookupKey] = lookupsResult[lookupKey].some(
            (item) => Array.isArray(item.category) && item.category.length
          );
          if (
            this.isLookupGrouped[lookupKey] &&
            lookupKey !== this.selectedDomainsKey
          ) {
            groupedLookups[lookupKey] =
              this.rulesUsageService.mapGroupedLookups(
                lookupsResult[lookupKey]
              );
          } else {
            groupedLookups[lookupKey] = [];
          }
        });

        this.updateLookupsSignal(this.groupedLookupsSignal, {
          ...groupedLookups,
        });
        this.updateLookupsSignal(this.groupedLookupsViewSignal, {
          ...groupedLookups,
        });

        const lookups = Object.keys(lookupsResult).reduce((acc, lookupKey) => {
          acc[lookupKey] =
            lookupKey !== this.selectedDomainsKey
              ? this.rulesUsageService.mapLookups(lookupsResult[lookupKey])
              : this.rulesUsageService.getOperatorsList(
                  lookupsResult[lookupKey],
                  this.realm
                );
          return acc;
        }, {} as Lookups);

        if (lookups[this.selectedDomainsKey]) {
          this.domainLookupsSignal.set(lookups[this.selectedDomainsKey]);
        }

        this.updateLookupsSignal(this.lookupsSignal, { ...lookups });
        this.updateLookupsSignal(this.lookupsViewSignal, { ...lookups });
      }),
      catchError((e) => {
        console.error('Failed to fetch lookups!', e);
        this.lookupsSignal.set({});
        this.groupedLookupsSignal.set({});
        this.lookupsViewSignal.set({});
        this.domainLookupsSignal.set([]);
        return of();
      })
    );
  }

  private handleRouteChange = () => {
    const params = new URLSearchParams(window.location.search);
    const templateId = params.get('templateId');
    const modificationId = params.get('modificationId');
    const id = modificationId || templateId;
    if (id) {
      const currentTemplate = (
        this.templateService.templatesList() as ReportTemplate[]
      )?.find((item) => item.template_id === id);
      this.templateSignal.set(currentTemplate);
      this.templateService.templateChangedSubject.next(
        currentTemplate as ReportTemplate
      );
    } else {
      this.clearTemplate();
    }
  };

  ngAfterViewInit() {
    this.shadowDomStyleInjectorService
      .injectNgPrimeStyles(this.el)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (injected) => this.attachedToShadowDom.set(injected),
        error: (err) => console.error(err),
      });
  }

  applyTemplate(template: ReportTemplate) {
    const allFields = this.allFieldsSignal();
    if (this.filterForm) {
      this.filterForm.patchValue(template.input_params.fieldValues);
    } else {
      this.buildFilterForm(
        allFields as ReDynamicFilters[],
        template.input_params.fieldValues
      );
    }
    this.handleScopes();
    this.filterForm.markAsDirty();
    this.filterForm.markAsUntouched();
  }

  updateReport(template: ReportTemplate) {
    this.applyTemplate(template);
    this.runState.set(false);
    this.showReportTable.set(false);
    this.handleLookupsOnFiltersChange$().subscribe(() => {
      this.isTemplateSaved.set(true);
      this.isLoadingLookups.set(false);
    });
  }

  processTemplateIdFromUrl$(): Observable<ReportTemplate | null> {
    const queryParams = new URLSearchParams(window.location.search);
    const templateId = queryParams.get('templateId');
    const modificationId = queryParams.get('modificationId');
    const id = modificationId || templateId;

    if (!id) {
      return of(null).pipe(tap(() => this.clearTemplate()));
    }

    return this.templateService.getTemplateById(this.reportId, id).pipe(
      takeUntilDestroyed(this.destroyRef),
      take(1),
      tap((template) => {
        if (template) {
          this.templateSignal.set(template);
          this.applyTemplate(template);
        } else {
          this.clearTemplate();
        }
      }),
      catchError(() => {
        this.clearTemplate();
        return of(null);
      })
    );
  }

  protected saveTemporaryTemplate() {
    const payload = {
      fieldValues: this.filterForm.value,
      allFields: this.allFieldsSignal(),
    };
    const currentTemplate = this.templateSignal();
    const baseTemplate = this.templateService.baseTemplate();

    if (!this.isTemplateSaved() || !currentTemplate) {
      this.templateService
        .saveTemplate(null, null, payload, this.reportId, null, true)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe((template) => {
          const params: ReportSearchParams =
            currentTemplate &&
            ((!currentTemplate.is_temporary &&
              currentTemplate.template_name &&
              template.template_id) ||
              (baseTemplate &&
                currentTemplate.is_temporary &&
                baseTemplate.template_id !== template.template_id))
              ? {
                  modificationId: template.template_id,
                  templateName:
                    baseTemplate?.template_name ||
                    currentTemplate.template_name,
                  templateId:
                    baseTemplate?.template_id || currentTemplate.template_id,
                }
              : {
                  modificationId: null,
                  templateName: null,
                  templateId: template.template_id,
                };
          this.templateService.setSearchParams(params);
          this.templateService.updateTemplateList(template);
          this.templateSignal.set(template);
          this.applyTemplate(template);
        });
    }
  }

  private buildFilterForm(
    all: ReDynamicFilters[],
    values?: Record<string, unknown>
  ) {
    this.filterForm = new FormGroup({});

    all.forEach((field) => {
      let def =
        values && !this.nullUndefinedOrEmpty(values[field.field_id])
          ? values[field.field_id]
          : !this.nullUndefinedOrEmpty(field['default_value'])
          ? field['default_value']
          : null;

      if (!def && field.field_id === this.selectedDomainsKey) {
        def = (this.domainLookupsSignal() as ReLookups[]).map((l) => l.value);
      }

      const validators = [];

      if (field.required) {
        validators.push(Validators.required);
      }

      this.filterForm.addControl(
        field.field_id,
        new FormControl(def, validators)
      );
    });

    if (values) {
      this.isTemplateSaved.set(true);
    }

    this.filterForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.isTemplateSaved.set(false);
      });
  }

  private buildTableColumns(columns: ReDynamicFilters[]) {
    this.tableColumns = columns
      .map((f, i) => ({
        name: f.field_name,
        field: f.field_id,
        headerTooltip: f.field_name,
        visible: true,
        cellClass: 'text-align-left',
        footerCellClass: '',
        minWidth: 200,
        sorting: true,
        orderNumber: f.order_number ?? i + 1,
      }))
      .sort((a, b) => (a.orderNumber > b.orderNumber ? 1 : -1));
  }

  protected runReport(offset: number, isLoadingMore = false) {
    this.showReportTable.set(true);
    this.offset = offset;
    const currentPage = this.getPageFromOffset(offset, this.limit());
    const pageKey =  this.getTableDataByParams(offset, this.limit(), currentPage);
    if (!isLoadingMore) {
      this.orderBy = null;
      this.orderDirection = null;
      this.tableData = [];
      this.cachedPages = {};
    } else if(Array.isArray(this.cachedPages[pageKey]) && this.cachedPages[pageKey].length) {
      this.tableData = [...this.cachedPages[pageKey]];
      return;
    }
    this.tableLoading.set(true);
    this.runState.set(true);
    this.cdr.detectChanges();

    const params = {
      offset,
      limit: this.limit(),
      allFilters: this.allFieldsSignal() as ReDynamicFilters[],
      formData: this.filterForm.value,
      executionType: 'report',
      sorting: { field: this.orderBy, value: this.orderDirection },
      template: this.templateSignal(),
      exportReportName: REPORTS.DM_RULES_USAGE.exportReportName,
    };
    this.rulesUsageService
      .runReport(params)
      .pipe(
        take(1),
        catchError((err) => {
          this.globalMessageService.showErrorMessage({
            detail: err || 'Failed to fetch data!',
          });
          return of({ rows: [], totals: { rows_count: 0 }, columns: [] });
        })
      )
      .subscribe((data: any) => {
        const { rows, totals, columns } = data;
        if (!isLoadingMore) {
          this.buildTableColumns(columns as ReDynamicFilters[]);
          this.generalTotal = totals.rows_count;
        }

        this.tableLoading.set(false);
        this.tableData = [...rows];
        this.cachedPages[pageKey] = [...rows];
        this.runState.set(false);
        this.cdr.detectChanges();
      });
  }

  private getPageFromOffset(offset: number, limit: number): number {
    return offset ? Math.floor(offset / limit) + 1 : 1;
  }

  private getTableDataByParams(offset: number, limit: number, page: number): string {
    return `page_${page}__offset_${offset}__limit${limit}`
  }

  protected onSort(sortingValue: {
    field: string | null;
    value: 'asc' | 'desc' | null;
  }): void {
    this.offset = 0;
    this.tableLoading.set(true);
    this.orderBy = sortingValue.field;
    this.orderDirection = sortingValue.value;
    this.cachedPages = {};
    this.rulesUsageService
      .runReport({
        offset: 0,
        limit: this.limit(),
        allFilters: this.allFieldsSignal() as ReDynamicFilters[],
        formData: this.filterForm.value,
        executionType: 'report',
        sorting: sortingValue,
        template: this.templateSignal(),
        exportReportName: REPORTS.DM_RULES_USAGE.exportReportName,
      })
      .pipe(
        take(1),
        catchError((err) => {
          this.globalMessageService.showErrorMessage({
            detail: err || 'Failed to fetch sorted data!',
          });
          return of({ rows: [] });
        })
      )
      .subscribe((data) => {
        const { rows } = data;
        this.tableData = [...rows];
        const currentPage = this.getPageFromOffset(0, this.limit());
        const pageKey =  this.getTableDataByParams(currentPage, this.limit(), currentPage);
        this.cachedPages[pageKey] = [...rows];
        this.tableLoading.set(false);
        this.cdr.detectChanges();
      });
  }

  protected downloadCsv(template?: ReportTemplate) {
    if (!template) this.csvLoading.set(true);
    this.rulesUsageService
      .runReport({
        offset: 0,
        limit: this.limit(),
        allFilters: template
          ? template.input_params.allFields
          : (this.allFieldsSignal() as ReDynamicFilters[]) || [],
        formData: template
          ? template.input_params.fieldValues
          : this.filterForm.value,
        executionType: 'csv_load',
        sorting: { field: null, value: null },
        template: template || this.templateSignal(),
        exportReportName: REPORTS.DM_RULES_USAGE.exportReportName,
      })
      .pipe(
        switchMap((data) => this.rulesUsageService.csvLoad(data?.[0]?.path)),
        take(1),
        finalize(() => {
          this.csvLoading.set(false);
          if (template) {
            this.templateCsvLoadingConfig.set({
              templateId: template.template_id,
              isLoading: false,
            });
          }
        })
      )
      .subscribe({
        next: (res) => {
          const templateName = `Report_${template?.template_name || (this.templateSignal() as ReportTemplate).template_name || '' }_${moment().format('YYYY-MM-DD_HH:mm')}.csv`;
          downloadURL(res, templateName);
        },
        error: (err) => {
          this.globalMessageService.showErrorMessage({
            detail: err || 'Failed to download CSV file!',
          });
        }
      });
  }

  protected getControl(fieldId: string): any {
    const control = this.filterForm.get(fieldId);
    return control instanceof FormControl ? control : null;
  }

  protected clearFilters(): void {
    const all = this.allFieldsSignal() || [];
    all.forEach((field) => {
      let def = !this.nullUndefinedOrEmpty(field['default_value'])
        ? field['default_value']
        : null;

      if (!def && field.field_id === this.selectedDomainsKey) {
        def = (this.domainLookupsSignal() as ReLookups[]).map((l) => l.value);
      }
      this.filterForm.get(field.field_id)?.reset(def);
    });

    this.handleLookupsOnFiltersChange$().subscribe(() => {
      this.handleScopes();
      this.filterForm.markAsPristine();
      this.filterForm.markAsUntouched();
      this.isLoadingLookups.set(false);
    });
  }

  protected openSaveTemplateModal() {
    this.domainsOverloadSaveMode.set(true);
    this.domainsOverloadAction = 'Save';
    if (this.isAllDomainsSelected) {
      this.domainsOverloadVisible.set(true);
    } else {
      this.modalVisible.set(true);
    }
  }

  protected runOverloadCheck(action: 'Run' | 'Download') {
    this.domainsOverloadSaveMode.set(false);
    if (this.isAllDomainsSelected) {
      this.domainsOverloadAction = action;
      this.domainsOverloadVisible.set(true);
    } else {
      if (action == 'Run') {
        this.saveTemporaryTemplate();
        this.runReport(0, false);
      } else if (action == 'Download') {
        this.downloadCsv();
      }
    }
  }

  protected domainsOverloadConfirm(event: {
    confirm: boolean;
    isSaveMode: boolean;
  }): void {
    const { confirm, isSaveMode } = event;
    if (confirm && isSaveMode) {
      this.modalVisible.set(true);
    } else if (confirm) {
      if (this.domainsOverloadAction == 'Run') {
        this.saveTemporaryTemplate();
        this.runReport(0, false);
      } else if (this.domainsOverloadAction == 'Download') {
        this.downloadCsv();
      }
    }
  }

  get isAllDomainsSelected(): boolean {
    if (
      !this.domainLookupsSignal()?.length ||
      this.domainLookupsSignal()?.length == 1 ||
      (this.sessionService.sessionData?.Domains &&
        this.sessionService.sessionData.Domains != '1000')
    )
      return false;
    return (
      this.domainLookupsSignal()?.length ===
      this.filterForm.get(this.selectedDomainsKey).value.length
    );
  }

  get isTemplateSavingEnabled(): boolean {
    return !this.isTemplateSaved() && !this.isLoadingLookups();
  }

  private clearTemplate() {
    if (this.filterForm) this.clearFilters();
    this.templateSignal.set(null);
    this.templateService.setSearchParams({
      modificationId: null,
      templateName: null,
      templateId: null,
    });
  }

  get templateLabel(): string {
    const queryParams = new URLSearchParams(window.location.search);
    const modificationId = queryParams.get('modificationId') || '';
    const templateName = queryParams.get('templateName') || '';
    return this.templateSignal()?.is_temporary && !modificationId
      ? ''
      : this.templateSignal()?.template_name ||
          (templateName ? TEMPORARY_TEMPLATE_WARNING + templateName : '');
  }

  private nullUndefinedOrEmpty(value: any): any {
    return value === null || value === undefined || value === '';
  }

  private updateLookupsSignal(
    signal: WritableSignal<any>,
    updated: Lookups | GroupedLookups
  ): void {
    if (!signal()) {
      signal.update((current) => ({ ...current, ...updated }));
    } else {
      signal.update((current) => ({ ...current, ...updated }));
    }
  }

  handleScopes() {
    const allFields = this.allFieldsSignal() || [];
    const allLookups = this.lookupsSignal() || {};
    const allGroupedLookups = this.groupedLookupsSignal() || {};
    const updatedLookups = structuredClone(allLookups);
    const updatedGroupedLookups = structuredClone(allGroupedLookups);
    const scopesIds = allFields
      .filter(({ field_group }) => field_group == 'scopes')
      .map(({ field_id }) => field_id);

    scopesIds.forEach((scope, i) => {
      const nextScopes = scopesIds.slice(i + 1);
      const prevScopes = scopesIds.slice(0, i);
      if (!this.nullUndefinedOrEmpty(this.filterForm.value[scope])) {
        nextScopes.forEach((nextScope) => {
          if (
            !this.nullUndefinedOrEmpty(this.filterForm.value[nextScope]) &&
            this.filterForm.value[scope] == this.filterForm.value[nextScope]
          ) {
            this.filterForm.get(nextScope).reset();
          }
        });
      }

      if (i == 0) {
        this.isScopeDisabled[scope] = false;
        updatedLookups[scope] = Array.isArray(allLookups[scope])
          ? allLookups[scope].slice()
          : [];
        updatedGroupedLookups[scope] = Array.isArray(allGroupedLookups[scope])
          ? allGroupedLookups[scope].slice()
          : [];
      } else {
        this.isScopeDisabled[scope] = this.nullUndefinedOrEmpty(
          this.filterForm.value[scopesIds[i - 1]]
        );
        if (this.isScopeDisabled[scope]) {
          updatedLookups[scope] = [];
          updatedGroupedLookups[scope] = [];
          if (!this.nullUndefinedOrEmpty(this.filterForm.value[scope])) {
            this.filterForm.get(scope).reset();
          }
        } else {
          updatedLookups[scope] = Array.isArray(allLookups[scope])
            ? allLookups[scope].filter(({ value }) =>
                prevScopes.every((key) => this.filterForm.value[key] !== value)
              )
            : [];

          updatedGroupedLookups[scope] = Array.isArray(allGroupedLookups[scope])
            ? allGroupedLookups[scope].filter(({ value }) =>
                prevScopes.every((key) => this.filterForm.value[key] !== value)
              )
            : [];
        }
      }
    });
    this.updateLookupsSignal(this.lookupsViewSignal, updatedLookups);
    this.updateLookupsSignal(this.groupedLookupsViewSignal, updatedGroupedLookups);
  }

  onSelectChange(field: ReDynamicFilters, value: any) {
    if (field.field_group == 'scopes') {
      this.handleScopes();
    }
    this.handleLookupsOnSelectChange(field, value);
  }

  handleLookupsOnSelectChange(field: ReDynamicFilters, value: any) {
    const allFields = this.allFieldsSignal() || [];
    const affectedFields = allFields.filter(
      ({ field_data_type, field_id, require_query_params }) =>
        field_data_type == 'enum' &&
        field_id != field.field_id &&
        require_query_params?.includes(field.field_id)
    );

    if (affectedFields.length) {
      if (!this.nullUndefinedOrEmpty(value)) {
        affectedFields.forEach(({ field_id }) => {
          this.isFieldLoading[field_id] = true;
        });
        this.isFieldLoading[field.field_id] = true;
        this.isLoadingLookups.set(true);
        this.sessionService.sessionData$
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            filter((data) => data !== null),
            take(1),
            switchMap(() =>
              this.loadLookups$(
                affectedFields,
                Object.assign({}, this.getQueryParams(this.filterForm.value), {
                  [field.field_id]: value,
                })
              )
            )
          )
          .subscribe(() => {
            affectedFields.forEach(({ field_id, multiselect }) => {
              this.isFieldLoading[field_id] = false;
              const lookup =
                this.lookupsByField()(
                  field_id,
                  this.isLookupGrouped[field_id]
                ) ?? [];
              if (!this.nullUndefinedOrEmpty(this.filterForm.value[field_id])) {
                if (
                  multiselect &&
                  Array.isArray(this.filterForm.value[field_id]) &&
                  this.filterForm.value[field_id].length
                ) {
                  const selectedIds = this.filterForm.value[field_id].filter(
                    (id) => {
                      return lookup.findIndex((l: any) => l.value == id) !== -1;
                    }
                  );
                  this.filterForm.get(field_id)?.setValue(selectedIds);
                } else if (
                  !multiselect &&
                  lookup.findIndex(
                    (l: any) => l.value == this.filterForm.value[field_id]
                  ) == -1
                ) {
                  this.filterForm.get(field_id)?.reset();
                }
              }
            });
            this.isFieldLoading[field.field_id] = false;
            this.isLoadingLookups.set(false);
          });
      } else {
        const lookups = structuredClone(this.lookupsViewSignal() || {});
        const groupedLookups = structuredClone(
          this.groupedLookupsViewSignal() || {}
        );
        affectedFields.forEach(({ field_id }) => {
          if (!this.nullUndefinedOrEmpty(this.filterForm.value[field_id])) {
            this.filterForm.get(field_id).reset();
          }
          delete lookups[field_id];
          delete groupedLookups[field_id];
        });
        this.updateLookupsSignal(this.lookupsViewSignal, lookups);
        this.updateLookupsSignal(this.groupedLookupsViewSignal, groupedLookups);
      }
    }
  }

  get realm(): string {
    return this.sessionService.sessionData?.realm || '/';
  }

  getQueryParams(
    values?: Record<string, unknown>,
    allFields: ReDynamicFilters[] = this.allFieldsSignal() || []
  ): AdditionalParams {
    const params: AdditionalParams = { realm: this.realm };
    const requiredQueryParams = allFields.reduce((acc, field) => {
      if (Array.isArray(field.require_query_params)) {
        field.require_query_params.forEach((p) => {
          if (!params[p] && !acc.includes(p)) {
            acc.push(p);
          }
        });
      }
      return acc;
    }, [] as string[]);

    requiredQueryParams.forEach((p) => {
      const field = allFields.find(({ field_id }) => field_id == p);

      const valueFromInput = values?.[p];
      const defaultValue = field?.['default_value'];
      const isValueValid = !this.nullUndefinedOrEmpty(valueFromInput);
      const isDefaultValid = !this.nullUndefinedOrEmpty(defaultValue);

      if (isValueValid || isDefaultValid) {
        params[p] = isValueValid ? valueFromInput : defaultValue;
      }
    });
    return params;
  }

  handleLookupsOnFiltersChange$() {
    const excludeParams = ['realm', this.selectedDomainsKey];
    const allFields = this.allFieldsSignal() || [];
    const affectedFields = allFields.filter(
      ({ field_data_type, require_query_params }) =>
        field_data_type == 'enum' && require_query_params?.length
    );

    const lookupsLoadFields = affectedFields.filter(
      ({ require_query_params }) =>
        require_query_params?.filter((p) => !excludeParams.includes(p)).length
    );
    if (affectedFields.length && lookupsLoadFields.length) {
      affectedFields.forEach(({ field_id }) => {
        this.isFieldLoading[field_id] = true;
      });
      this.isLoadingLookups.set(true);
      return this.sessionService.sessionData$.pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((data) => data !== null),
        take(1),
        switchMap(() =>
          this.loadLookups$(
            lookupsLoadFields,
            this.getQueryParams(this.filterForm.value)
          )
        ),
        tap(() => {
          affectedFields.forEach(({ field_id, multiselect }) => {
            this.isFieldLoading[field_id] = false;
            const lookups = this.lookupsViewSignal();
            const lookup = lookups?.[field_id] ?? [];
            if (!this.nullUndefinedOrEmpty(this.filterForm.value[field_id])) {
              if (
                multiselect &&
                Array.isArray(this.filterForm.value[field_id]) &&
                this.filterForm.value[field_id].length
              ) {
                const selectedIds = this.filterForm.value[field_id].filter(
                  (id) => {
                    return (
                      lookup.findIndex((l: ReLookups) => l.value == id) !== -1
                    );
                  }
                );

                this.filterForm.get(field_id)?.setValue(selectedIds);
              } else if (
                !multiselect &&
                lookup.findIndex(
                  (l: ReLookups) => l.value == this.filterForm.value[field_id]
                ) == -1
              ) {
                this.filterForm.get(field_id)?.reset();
              }
            }
          });
        })
      );
    } else {
      return of();
    }
  }

  trackById(_: number, field: ReDynamicFilters) {
    if (field.field_data_type !== 'enum') {
      return field.field_id;
    }
    const groupingState = this.isLookupGrouped[field.field_id]
      ? 'grouped'
      : 'ungrouped';
    return `${field.field_id}_${groupingState}`;
  }
}